import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';

/**
 * A LONG LIST THAT DRAWS ONLY WHAT IS IN VIEW.
 *
 * A feature has a hundred and twenty scenes and a series episode can have three hundred; drawing
 * every row of them re-renders hundreds of nodes on every pick. This draws the rows in view plus a
 * few either side, in a box as tall as the whole list, so the scrollbar still says how long the
 * list is. No dependency: fixed-height rows make the arithmetic a division.
 *
 * ONE LISTBOX, NOT A COLUMN OF BUTTONS. Focus stays on the list itself and
 * `aria-activedescendant` names the selected option, so a row scrolled out of view and unmounted
 * never takes focus with it. Every option carries `aria-setsize` and `aria-posinset`, so a screen
 * reader says "scene 41 of 300" although only twenty rows exist. ↑/↓ move, Home/End go to the
 * ends, Page Up/Down move a screenful; the selection follows and is scrolled into view.
 *
 * THE SELECTED ROW IS ALWAYS MOUNTED, wherever the list is scrolled, so the descendant the list
 * names always exists.
 */
export function WindowedList<T>({
  items,
  getKey,
  rowHeight,
  selectedKey,
  onSelect,
  renderRow,
  label,
  testId,
  rowTestId,
  overscan = 6,
  fallbackHeight = 600,
  empty,
}: {
  items: readonly T[];
  getKey: (item: T) => string;
  /** Every row's height in pixels; the rows are drawn exactly this tall. */
  rowHeight: number;
  selectedKey: string | null;
  onSelect: (key: string) => void;
  renderRow: (item: T, state: { readonly selected: boolean; readonly index: number }) => ReactNode;
  label: string;
  testId: string;
  rowTestId?: (item: T) => string;
  /** Rows drawn beyond each edge of the view. */
  overscan?: number;
  /** The view's height before it can be measured (and where nothing is laid out, as in a test). */
  fallbackHeight?: number;
  /** Said in place of the list when it has no rows. */
  empty?: ReactNode;
}) {
  const baseId = useId();
  const box = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [height, setHeight] = useState(fallbackHeight);

  useLayoutEffect(() => {
    const node = box.current;
    if (node === null) return;
    const measure = () => {
      setHeight(node.clientHeight > 0 ? node.clientHeight : fallbackHeight);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [fallbackHeight]);

  const count = items.length;
  const selectedIndex = selectedKey === null ? -1 : items.findIndex((item) => getKey(item) === selectedKey);
  const optionId = (index: number) => `${baseId}-option-${String(index)}`;

  /** Keep a row in view: scroll the least that shows it whole. */
  const reveal = useCallback(
    (index: number) => {
      const node = box.current;
      if (node === null || index < 0) return;
      const top = index * rowHeight;
      const bottom = top + rowHeight;
      const view = node.clientHeight > 0 ? node.clientHeight : height;
      if (top < node.scrollTop) node.scrollTop = top;
      else if (bottom > node.scrollTop + view) node.scrollTop = bottom - view;
      setScrollTop(node.scrollTop);
    },
    [rowHeight, height],
  );

  // A selection made anywhere (a link, "Review next scene", the address) is brought into view —
  // only when the selection moves, not on every re-render of the list.
  const revealRef = useRef(reveal);
  revealRef.current = reveal;
  useEffect(() => {
    revealRef.current(selectedIndex);
  }, [selectedKey, selectedIndex, count]);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (count === 0) return;
    const page = Math.max(1, Math.floor(height / rowHeight) - 1);
    let next: number | null = null;
    if (event.key === 'ArrowDown') next = selectedIndex < 0 ? 0 : Math.min(count - 1, selectedIndex + 1);
    else if (event.key === 'ArrowUp') next = selectedIndex < 0 ? 0 : Math.max(0, selectedIndex - 1);
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = count - 1;
    else if (event.key === 'PageDown') next = Math.min(count - 1, Math.max(0, selectedIndex) + page);
    else if (event.key === 'PageUp') next = Math.max(0, selectedIndex - page);
    if (next === null) return;

    event.preventDefault();
    const item = items[next];
    if (item === undefined) return;
    onSelect(getKey(item));
    reveal(next);
  };

  if (count === 0) {
    return (
      <div className="ui-wlist ui-wlist-empty" data-testid={testId}>
        {empty}
      </div>
    );
  }

  const first = Math.max(0, Math.floor(scrollTop / rowHeight) - overscan);
  const last = Math.min(count - 1, Math.ceil((scrollTop + height) / rowHeight) + overscan);
  const indexes: number[] = [];
  for (let i = first; i <= last; i += 1) indexes.push(i);
  if (selectedIndex >= 0 && (selectedIndex < first || selectedIndex > last)) indexes.push(selectedIndex);

  return (
    <div
      ref={box}
      className="ui-wlist"
      role="listbox"
      tabIndex={0}
      aria-label={label}
      aria-activedescendant={selectedIndex >= 0 ? optionId(selectedIndex) : undefined}
      data-testid={testId}
      onKeyDown={onKeyDown}
      onScroll={(event) => {
        setScrollTop(event.currentTarget.scrollTop);
      }}
    >
      <div className="ui-wlist-sizer" style={{ height: count * rowHeight }}>
        {indexes.map((index) => {
          const item = items[index] as T;
          const key = getKey(item);
          const selected = index === selectedIndex;

          return (
            <div
              key={key}
              id={optionId(index)}
              role="option"
              aria-selected={selected}
              aria-setsize={count}
              aria-posinset={index + 1}
              className="ui-wlist-row"
              data-selected={selected ? '1' : '0'}
              data-testid={rowTestId?.(item)}
              style={{ top: index * rowHeight, height: rowHeight }}
              onClick={() => {
                onSelect(key);
                box.current?.focus({ preventScroll: true });
              }}
            >
              {renderRow(item, { selected, index })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
